/**
 * `DevQuickSignIn` — localhost-only one-click sign-in helpers for the
 * Clerk-hosted form during local dev. Mirrors
 * `lexide/src/components/dev/DevQuickSignIn.tsx` (same logic; per-app
 * styling).
 *
 * Two contextual buttons (URL-aware):
 *   - On `/sign-in`:                "Sign in with {name}" — fills the
 *                                   email input + clicks Continue.
 *   - On `/sign-in/factor-one`:     "Inject 424242 code" — fills the
 *                                   verification-code input + clicks
 *                                   Continue (Clerk's fixed test-mode
 *                                   code per ai-docs/login-credentials.md
 *                                   §A.1 / §C.1).
 *
 * Explicit two-step rather than auto-walk because Clerk's factor-one
 * mounts the OTP input slightly before its `prepareFirstFactor`
 * handshake binds React state — a fire-and-forget walk races that
 * binding. Letting the operator drive each step removes the race.
 *
 * Renders only when BOTH `import.meta.env.DEV` is true AND the hostname
 * is localhost/127.0.0.1.
 */

import { useEffect, useState } from "react";

export interface QuickSignInUser {
  /** Display label rendered as `Sign in with {label}` (e.g. "lexportal"). */
  label: string;
  /** Full Clerk identifier — typically a `+clerk_test@…` email. */
  identifier: string;
}

interface DevQuickSignInProps {
  users: QuickSignInUser[];
}

const TEST_MODE_CODE = "424242";

function isLocalhost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

function useLocationPath(): string {
  const [path, setPath] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );
  useEffect(() => {
    const id = setInterval(() => {
      if (window.location.pathname !== path) setPath(window.location.pathname);
    }, 200);
    return () => clearInterval(id);
  }, [path]);
  return path;
}

async function waitForElement<T extends Element>(
  selector: string,
  timeoutMs = 8000,
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const el = document.querySelector<T>(selector);
    if (el) return el;
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error(`timed out waiting for ${selector}`);
}

/** Wait for the SAME element matching `selector` to remain in the DOM
 *  continuously for `stableMs`. Used to ride out the email→OTP screen
 *  flicker (the post-`8b31f781` RequireOrgUser / auth-exchange kick can
 *  remount the factor-one form once or twice before it settles). */
async function waitForStableElement<T extends Element>(
  selector: string,
  stableMs: number,
  timeoutMs: number,
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  let lastEl: T | null = null;
  let stableSince = 0;
  while (Date.now() < deadline) {
    const el = document.querySelector<T>(selector);
    if (el && el === lastEl && document.contains(el)) {
      if (Date.now() - stableSince >= stableMs) return el;
    } else {
      lastEl = el;
      stableSince = Date.now();
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error(`timed out waiting for ${selector} to be stable`);
}

function setReactInputValue(input: HTMLInputElement, value: string): void {
  const proto = Object.getPrototypeOf(input);
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (!setter) input.value = value;
  else setter.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

/** Type a string into an input one character at a time with realistic
 *  delays + full keystroke events. Clerk's OTP input has bot-detection
 *  heuristics that silently reject instant value-sets; per-character
 *  typing with keydown/keypress/input/keyup events looks like a real
 *  user and gets through. */
async function typeIntoInput(input: HTMLInputElement, text: string): Promise<void> {
  input.focus();
  for (const ch of text) {
    input.dispatchEvent(
      new KeyboardEvent("keydown", { key: ch, bubbles: true, cancelable: true }),
    );
    input.dispatchEvent(
      new KeyboardEvent("keypress", { key: ch, bubbles: true, cancelable: true }),
    );
    setReactInputValue(input, input.value + ch);
    input.dispatchEvent(
      new KeyboardEvent("keyup", { key: ch, bubbles: true, cancelable: true }),
    );
    await new Promise((r) => setTimeout(r, 70 + Math.random() * 40));
  }
}

function clickPrimary(form: HTMLFormElement | null): boolean {
  if (!form) return false;
  const primary =
    form.querySelector<HTMLButtonElement>(
      'button[data-localization-key="formButtonPrimary"]',
    ) ??
    form.querySelector<HTMLButtonElement>('button[type="submit"]') ??
    form.querySelector<HTMLButtonElement>("button:not([disabled])");
  if (!primary) return false;
  primary.click();
  return true;
}

export function DevQuickSignIn(props: DevQuickSignInProps) {
  if (!import.meta.env.DEV) return null;
  if (!isLocalhost()) return null;
  return <DevQuickSignInInner {...props} />;
}

function DevQuickSignInInner({ users }: DevQuickSignInProps) {
  const path = useLocationPath();
  const onFactorOne = path.includes("/sign-in/factor");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function fillEmailAndSubmit(identifier: string) {
    setError(null);
    setBusy(identifier);
    try {
      const emailInput = await waitForElement<HTMLInputElement>(
        'input[name="identifier"]',
      );
      setReactInputValue(emailInput, identifier);
      await new Promise((r) => setTimeout(r, 50));
      if (!clickPrimary(emailInput.closest("form"))) {
        throw new Error("Couldn't find the Continue button on the email step");
      }
    } catch (e: unknown) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function injectCodeAndSubmit() {
    setError(null);
    setBusy("__code__");
    try {
      // Wait for the same OTP input to be stable in the DOM for ~1s
      // before typing, so we don't fire keystrokes against a node that's
      // about to be remounted by the email→OTP flicker.
      const codeInput = await waitForStableElement<HTMLInputElement>(
        'input[name="code"], input[inputmode="numeric"]',
        1000,
        12000,
      );
      // Type character-by-character so Clerk's bot-detection heuristics
      // don't silently reject the OTP (instant value-sets get flagged).
      await typeIntoInput(codeInput, TEST_MODE_CODE);
      await new Promise((r) => setTimeout(r, 100));
      clickPrimary(codeInput.closest("form"));
    } catch (e: unknown) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      data-testid="dev-quick-signin"
      className="mb-4 w-full rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 p-3"
    >
      <div className="mb-2 text-[0.6875rem] font-medium uppercase tracking-[0.06em] text-amber-600 dark:text-amber-400">
        Dev quick sign-in · localhost only
      </div>
      {onFactorOne ? (
        <button
          type="button"
          onClick={injectCodeAndSubmit}
          disabled={busy != null}
          data-testid="dev-quick-signin-code"
          className="rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[0.8125rem] text-slate-800 transition-opacity hover:bg-amber-500/20 disabled:cursor-default dark:text-slate-100"
        >
          {busy === "__code__" ? "Injecting…" : "Inject 424242 code"}
        </button>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {users.map((u) => (
            <button
              key={u.identifier}
              type="button"
              onClick={() => fillEmailAndSubmit(u.identifier)}
              disabled={busy != null}
              data-testid={`dev-quick-signin-${u.label}`}
              className={
                "rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[0.8125rem] text-slate-800 transition-opacity hover:bg-amber-500/20 disabled:cursor-default dark:text-slate-100" +
                (busy != null && busy !== u.identifier ? " opacity-55" : "")
              }
            >
              {busy === u.identifier ? "Signing in…" : `Sign in with ${u.label}`}
            </button>
          ))}
        </div>
      )}
      {error && (
        <div role="alert" className="mt-2 break-words text-xs text-red-500 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}
